import React, {useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import constants from 'helpers/constants';
import DialogContent from 'components/atoms/Dialogs/DialogContent/DialogContent';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import DialogFooter from 'components/molecules/Dialogs/DialogFooter/DialogFooter';
import Button from 'components/atoms/Buttons/Button/Button';
import DialogHeader from 'components/molecules/Dialogs/DialogHeader/DialogHeader';
import {Span} from 'components/atoms/Text/Typography/Typography';
import StyledUserAddDialog from 'components/organisms/Dialogs/UserAddDialog/UserAddDialog.styles';
import utils from 'helpers/utils';
import {useAuthorize, useAuthUsers} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Close from '@mui/icons-material/Close';
import Add from '@mui/icons-material/Add';

const UserAddDialog = React.forwardRef((props, ref) => {
  const {
    onSubmit,
    ...innerProps
  } = useComponentProps(props, 'UserAddDialog', {
    children: ['header', 'content', 'footer']
  });

  const innerRef = useRef(null);
  const formRef = useRef(null);

  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(null);
  const [validation, setValidation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useImperativeHandle(ref, () => innerRef.current);

  const authorize = useAuthorize();
  const users = useAuthUsers();

  const fields = useMemo(() => {
    const roles = constants.data.roles
      .filter((v) => {
        return authorize({
          attribute: `user.field.authGroup.create.${v.value}`
        })
      });

    return [
      {
        name: 'firstName',
        label: 'First name',
        inlineLabel: 'first name',
        type: constants.formFieldTypes.text,
        validation: constants.formFieldValidationTypes.text,
        initial: '',
        required: true,
        FormFieldProps: {
          variant: 'staticLabel'
        },
      },
      {
        name: 'lastName',
        label: 'Last name',
        inlineLabel: 'last name',
        type: constants.formFieldTypes.text,
        validation: constants.formFieldValidationTypes.text,
        initial: '',
        required: true,
        FormFieldProps: {
          variant: 'staticLabel'
        },
      },
      {
        name: 'username',
        label: 'Username',
        inlineLabel: 'username',
        type: constants.formFieldTypes.text,
        validation: [constants.formFieldValidationTypes.username,
          `${constants.formFieldValidationTypes.min}(3)`, `${constants.formFieldValidationTypes.max}(20)`],
        validate: (value, testContext) => {
          const duplicate = users.find((u) => u.username?.toLowerCase() === value.toLowerCase());

          if (duplicate) {
            return testContext.createError({message: 'Duplicate username'});
          } else {
            return true;
          }
        },
        initial: '',
        required: true,
        FormFieldProps: {
          variant: 'staticLabel'
        },
      },
      {
        name: 'email',
        label: 'Email',
        inlineLabel: 'email',
        type: constants.formFieldTypes.text,
        validation: constants.formFieldValidationTypes.email,
        validate: (value, testContext) => {
          const duplicate = users.find((u) => u.email?.toLowerCase() === value.toLowerCase());

          if (duplicate) {
            return testContext.createError({message: 'Duplicate email'});
          } else {
            return true;
          }
        },
        initial: '',
        required: true,
        FormFieldProps: {
          variant: 'staticLabel'
        },
      },
      {
        name: 'authGroup',
        label: 'Role',
        inlineLabel: 'role',
        type: constants.formFieldTypes.autocomplete,
        validation: constants.formFieldValidationTypes.text,
        conversion: constants.formFieldConversionTypes.value,
        options: roles,
        initial: '',
        required: true,
        FormFieldProps: {
          variant: 'staticLabel'
        },
      },
    ]
  }, [authorize, users]);

  const handleSubmit = (values, actions) => {
    setSubmitting(true);

    utils.asPromise(onSubmit)(values)
      .then(() => {
        innerProps.onClose?.(null, 'saveButtonClick');
      })
      .catch(() => {
        setError('Creating user failed');
      })
      .finally(() => {
        actions.setSubmitting(false);
        setSubmitting(false);
      });
  };

  const handleValidating = (isValidating, isDirty, hasErrors) => {
    setDirty(isDirty);
    if (hasErrors) {
      setValidation('Please check if all fields have the correct values');
    } else {
      setValidation(null);
    }
  }

  const handleSubmitClick = () => {
    formRef.current?.submit();
  };

  const handleCancel = (e) => {
    innerRef.current?.close?.(e, 'cancelButtonClick');
  }

  const handleClose = (e, reason) => {
    if ((!submitting && !error && !dirty) || ['escapeKeyDown', 'closeButtonClick', 'cancelButtonClick'].includes(reason)) {
      innerProps.onClose?.(e, reason);
    }
  }

  const renderButtons = () => {
    return <React.Fragment>
      <Button children={'Cancel'}
              variant="text"
              startIcon={<Icon icon={Close}/>}
              onClick={handleCancel}/>
      <Button disabled={submitting}
              type="submit"
              color="success"
              variant="contained"
              children={'Create user'}
              startIcon={<Icon icon={Add}/>}
              onClick={handleSubmitClick}/>
    </React.Fragment>
  }

  return <StyledUserAddDialog ref={innerRef} {...innerProps} onClose={handleClose}>
    <DialogHeader className="UserAddDialog-header"
                  title="Add user" />
    <DialogContent className="UserAddDialog-content" >
      <InlineForm ref={formRef} fields={fields}
                  onValidating={handleValidating}
                  onSubmit={handleSubmit} />
    </DialogContent>
    <DialogFooter className="UserAddDialog-footer"
                  info={(error ?? validation) ? <Span color="error">{error ?? validation}</Span> : null}
                  buttons={renderButtons()} />
  </StyledUserAddDialog>
});

UserAddDialog.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  onSubmit: PropTypes.func
};

export default UserAddDialog;
